"use client";

import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { abonneerFilmStand, bewegingIsUit, meldDealFilm } from "@/lib/dealfilm";

// Stille film over de foto van een dealkaart (Dennis 26-10-2026). De foto
// blijft het LCP-element: de `src` komt pas na `load` en zodra de kaart binnen
// ~300px van het scherm komt; spelen vanaf ~25% in beeld, zonder limiet op het
// aantal kaarten. Alles behalve een werkende film = alleen foto.

/** Mag er een film? Niet bij reduced-motion, saveData of een 2g-lijn. */
export function magKaartFilm() {
  if (typeof window === "undefined") return true;
  try {
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return false;
    const c =window.navigator && window.navigator.connection;
    if (c && c.saveData) return false;
    if (c && typeof c.effectiveType === "string" && c.effectiveType.includes("2g")) return false;
  } catch {
    return false;
  }
  return true;
}

function naLoad(cb) {
  if (document.readyState === "complete") {
    cb();
    return () => {};
  }
  window.addEventListener("load", cb, { once: true });
  return () => window.removeEventListener("load", cb);
}

const HERSTEL_GEBEURTENISSEN = ["pointerdown", "touchstart", "click", "scroll", "keydown"];

// `meet` ({ dealsId, plek, groep }) alleen voor films uit het register: dan
// meldt de kaart dealfilm_getoond / dealfilm_fout in de dataLayer.
export default function KaartFilm({ mp4, poster, meet }) {
  // Server: film toegestaan; de client corrigeert na hydratie zonder effect-setState.
  // "Beweging uit" (FILM_UIT_KEY) stopt ook films die al spelen of later verschijnen.
  const mag = useSyncExternalStore(abonneerFilmStand, () => magKaartFilm() && !bewegingIsUit(), () => true);
  const [fout, setFout] = useState(false);
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef(null);
  const gemeldRef = useRef(false);

  useEffect(() => {
    if (!mag || fout || typeof IntersectionObserver !== "function") return undefined;
    let laadIo = null;
    let speelIo = null;
    let gemonteerd = null;
    let inBeeld = false;
    let herstel = null;
    // Per effect: na cleanup of uit beeld doet een late play()-afwijzing niets meer.
    let actief = true;
    let generatie = 0;

    const stopHerstel = () => {
      if (!herstel) return;
      const ac = herstel;
      herstel = null;
      ac.abort();
    };
    const zetSrc = (v) => {
      if (v.getAttribute("src")) return;
      v.setAttribute("preload", "auto");
      v.setAttribute("src", mp4);
    };
    // Geweigerd (bv. iPhone in energiebesparing): één keer opnieuw bij de eerstvolgende
    // aanraking, klik of scroll van de bezoeker; lukt het dan niet, dan blijft de foto.
    const probeerOpnieuw = (v) => {
      if (herstel || !actief || !inBeeld) return;
      const ac = new AbortController();
      herstel = ac;
      const h = () => {
        stopHerstel();
        if (!actief || !inBeeld) return;
        generatie += 1;
        try {
          const p = v.play();
          if (p && p.catch) p.catch(() => {});
        } catch {
          // foto blijft
        }
      };
      HERSTEL_GEBEURTENISSEN.forEach((naam) =>
        document.addEventListener(naam, h, { once: true, passive: true, signal: ac.signal }),
      );
    };
    const speel = (v) => {
      generatie += 1;
      const mijn = generatie;
      const nogGeldig = () => actief && inBeeld && mijn === generatie;
      try {
        zetSrc(v);
        const p = v.play();
        if (p && p.catch) {
          p.catch(() => {
            if (nogGeldig()) probeerOpnieuw(v);
          });
        }
      } catch {
        if (nogGeldig()) probeerOpnieuw(v);
      }
    };

    const stopWachten = naLoad(() => {
      const video = videoRef.current;
      if (!video) return;
      gemonteerd = video;
      const doel = video.parentElement || video;
      // Laden: net vóór de kaart in beeld komt (binnen ~300px van het scherm).
      laadIo = new IntersectionObserver(
        (entries) => {
          const last = entries[entries.length - 1];
          const v = videoRef.current;
          if (!last || !v || !last.isIntersecting) return;
          try {
            zetSrc(v);
          } catch {
            // foto blijft
          }
        },
        { rootMargin: "300px 0px 300px 0px" },
      );
      // Spelen: vanaf ~25% in beeld, voor elke kaart tegelijk; uit beeld pauzeren.
      speelIo = new IntersectionObserver(
        (entries) => {
          const last = entries[entries.length - 1];
          const v = videoRef.current;
          if (!last || !v) return;
          try {
            // isIntersecting is al waar bij 1 pixel: een kwart telt via de ratio.
            if (last.isIntersecting && last.intersectionRatio >= 0.25) {
              inBeeld = true;
              speel(v);
            } else {
              inBeeld = false;
              generatie += 1;
              stopHerstel();
              v.pause();
            }
          } catch {
            // autoplay geweigerd: foto blijft
          }
        },
        { threshold: 0.25 },
      );
      laadIo.observe(doel);
      speelIo.observe(doel);
    });
    return () => {
      actief = false;
      generatie += 1;
      stopWachten();
      stopHerstel();
      if (laadIo) laadIo.disconnect();
      if (speelIo) speelIo.disconnect();
      // Film weg (reduced-motion, "Beweging uit"): eerst pauzeren, dan verdwijnt hij.
      try {
        if (gemonteerd) gemonteerd.pause();
      } catch {
        // niets te pauzeren
      }
    };
  }, [mag, fout, mp4]);

  if (!mag || fout) return null;

  return (
    <video
      ref={videoRef}
      poster={poster}
      muted
      loop
      playsInline
      preload="none"
      disablePictureInPicture
      aria-hidden="true"
      className="absolute inset-0 w-full h-full object-cover pointer-events-none"
      style={{ pointerEvents: "none", opacity: playing ? 1 : 0, transition: "opacity 600ms ease" }}
      onPlaying={() => {
        setPlaying(true);
        if (meet && !gemeldRef.current) {
          gemeldRef.current = true;
          meldDealFilm("getoond", meet);
        }
      }}
      onError={() => {
        setFout(true);
        if (meet && !gemeldRef.current) {
          gemeldRef.current = true;
          meldDealFilm("fout", meet);
        }
      }}
    />
  );
}
